import type { Experiment } from 'types';
import { isPreStart } from 'helpers/experiment-status';

/**
 * What Home shows before it has numbers to show: the first-run steps for a website with
 * nothing live, and the experiments that are running but have not recorded anyone yet.
 *
 * Pure so the thresholds and step logic can be tested without rendering.
 */

/** How long a running experiment may wait for its first visitor before Home calls it out. */
export const WAITING_WARN_MS = 60 * 60 * 1000;

export type SetupStepKey = 'website' | 'snippet' | 'create' | 'start';

export type SetupStep = { key: SetupStepKey; done: boolean };

type SetupStepsParams = { hasWebsite: boolean; experiments: readonly Experiment[] };

const sessionsOf = (experiment: Experiment) => Number(experiment.sessions ?? 0);

/** Running and has not recorded a single session yet. */
export const isAwaitingFirstVisitor = (experiment: Experiment) =>
  experiment.status === 'Running' && sessionsOf(experiment) === 0;

/**
 * The road from an empty website to a running experiment.
 *
 * The snippet cannot be checked directly: the runtime only reports while an experiment
 * is running, so it is confirmed by any experiment on this website having recorded a
 * session, and otherwise left open rather than guessed at.
 */
export function setupSteps({ hasWebsite, experiments }: SetupStepsParams): SetupStep[] {
  const isLive = (experiment: Experiment) => experiment.status === 'Running' || experiment.status === 'Deployed';

  return [
    { key: 'website', done: hasWebsite },
    { key: 'snippet', done: experiments.some((experiment) => sessionsOf(experiment) > 0) },
    { key: 'create', done: experiments.length > 0 },
    { key: 'start', done: experiments.some(isLive) },
  ];
}

/** The first step still open, which is the one that carries the primary button. */
export const currentStep = (steps: readonly SetupStep[]): SetupStepKey | null =>
  steps.find((step) => !step.done)?.key ?? null;

/** The draft the "open it" buttons go to: the newest one. */
export const latestDraft = (experiments: readonly Experiment[]): Experiment | null =>
  experiments
    .filter((experiment) => isPreStart(experiment.status))
    .reduce<Experiment | null>(
      (newest, experiment) =>
        !newest || Number(experiment.createdAt) > Number(newest.createdAt) ? experiment : newest,
      null,
    );

export type WaitingExperiment = { experiment: Experiment; waitedMs: number; isOverdue: boolean };

/**
 * Running experiments that have not recorded a single session.
 *
 * Waiting is measured from the start date, falling back to creation for an experiment
 * started before start dates were recorded. Past WAITING_WARN_MS the wait stops being
 * normal; past a day the attention card takes over with its own no-data item.
 */
export function waitingExperiments(experiments: readonly Experiment[], now: number): WaitingExperiment[] {
  return experiments.filter(isAwaitingFirstVisitor).map((experiment) => {
    const startedAt = Number(experiment.startsAt ?? experiment.createdAt ?? now);
    const waitedMs = Math.max(0, now - startedAt);
    return { experiment, waitedMs, isOverdue: waitedMs >= WAITING_WARN_MS };
  });
}

/** "4 minutes", "1 hour", "3 days": the wait in the largest unit that fits. */
export function formatWait(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return 'less than a minute';
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}
